/**
 * Reasoning Component Utilities
 *
 * Helper functions for working with the Reasoning Component
 */

import { ProcessStep, ToolCall, ReasoningData, ProcessStepStatus, ToolCallStatus } from './types'

/**
 * Creates an initial thinking data object
 */
export const createInitialReasoningData = (): ReasoningData => ({
  isThinking: false,
  elapsedTime: 0,
  processSteps: [],
  toolCalls: []
})

/**
 * Calculates elapsed time since thinking started
 */
export const calculateElapsedTime = (startTime?: number): number => {
  if (!startTime) return 0
  return Math.floor((Date.now() - startTime) / 1000)
}

/**
 * Formats elapsed time to human-readable string
 */
export const formatElapsedTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`
  }
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

/**
 * Creates a new process step
 */
export const createProcessStep = (
  id: string,
  text: string,
  status: ProcessStepStatus = 'pending'
): ProcessStep => ({
  id,
  text,
  status
})

/**
 * Creates a new tool call
 */
export const createToolCall = (
  id: string,
  type: ToolCall['type'],
  action: string,
  context: string,
  status: ToolCallStatus = 'pending',
  details?: string
): ToolCall => ({
  id,
  type,
  action,
  context,
  status,
  details
})

/**
 * Updates a process step status
 */
export const updateProcessStepStatus = (
  steps: ProcessStep[],
  stepId: string,
  newStatus: ProcessStepStatus
): ProcessStep[] => {
  return steps.map((step) => {
    if (step.id === stepId) {
      return { ...step, status: newStatus }
    }
    return step
  })
}

/**
 * Updates a tool call status
 */
export const updateToolCallStatus = (
  toolCalls: ToolCall[],
  toolCallId: string,
  newStatus: ToolCallStatus
): ToolCall[] => {
  return toolCalls.map((toolCall) => {
    if (toolCall.id === toolCallId) {
      return { ...toolCall, status: newStatus }
    }
    return toolCall
  })
}

/**
 * Adds a process step to the list
 */
export const addProcessStep = (steps: ProcessStep[], step: ProcessStep): ProcessStep[] => {
  return [...steps, step]
}

/**
 * Adds a tool call to the list
 */
export const addToolCall = (toolCalls: ToolCall[], toolCall: ToolCall): ToolCall[] => {
  return [...toolCalls, toolCall]
}

/**
 * Gets the currently active process step
 */
export const getActiveProcessStep = (steps: ProcessStep[]): ProcessStep | undefined => {
  return steps.find((step) => step.status === 'active')
}

/**
 * Gets all running tool calls
 */
export const getRunningToolCalls = (toolCalls: ToolCall[]): ToolCall[] => {
  return toolCalls.filter((toolCall) => toolCall.status === 'running')
}

/**
 * Checks if all process steps are completed
 */
export const areAllStepsCompleted = (steps: ProcessStep[]): boolean => {
  return steps.length > 0 && steps.every((step) => step.status === 'completed')
}

/**
 * Checks if all tool calls are completed
 */
export const areAllToolCallsCompleted = (toolCalls: ToolCall[]): boolean => {
  return toolCalls.length > 0 && toolCalls.every((toolCall) => toolCall.status === 'completed')
}

/**
 * Gets progress percentage based on completed steps
 */
export const getProgressPercentage = (steps: ProcessStep[]): number => {
  if (steps.length === 0) return 0
  const completedCount = steps.filter((step) => step.status === 'completed').length
  return Math.round((completedCount / steps.length) * 100)
}

/**
 * Parses SSE thinking data from streaming response
 * This is a placeholder - implement based on your actual SSE format
 */
export const parseReasoningFromSSE = (eventData: any): Partial<ReasoningData> | null => {
  try {
    // Example implementation - adjust based on your actual SSE format
    if (eventData.type === 'thinking_step') {
      return {
        processSteps: [createProcessStep(eventData.stepId, eventData.text, eventData.status)]
      }
    }

    if (eventData.type === 'tool_call') {
      return {
        toolCalls: [
          createToolCall(
            eventData.toolId,
            eventData.toolType,
            eventData.action,
            eventData.context,
            eventData.status,
            eventData.details
          )
        ]
      }
    }

    return null
  } catch (error) {
    console.error('Error parsing reasoning data from SSE:', error)
    return null
  }
}

/**
 * Merges new thinking data with existing data
 */
export const mergeReasoningData = (
  current: ReasoningData,
  update: Partial<ReasoningData>
): ReasoningData => {
  return {
    ...current,
    ...update,
    processSteps: update.processSteps
      ? [...current.processSteps, ...update.processSteps]
      : current.processSteps,
    toolCalls: update.toolCalls ? [...current.toolCalls, ...update.toolCalls] : current.toolCalls
  }
}

/**
 * Resets thinking data to initial state
 */
export const resetReasoningData = (): ReasoningData => createInitialReasoningData()

/**
 * Generates a unique ID for process steps and tool calls
 */
export const generateReasoningId = (prefix: 'step' | 'tool' = 'step'): string => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * When a tool result wraps a human-readable body (e.g. a fetched page returned
 * as `{ url, status, format: 'markdown', content: '…' }`), pull out that body so
 * it can be shown as real markdown instead of a JSON dump full of escaped `\n`.
 * Returns undefined when there is no obvious readable body.
 */
const extractReadableBody = (parsed: unknown): string | undefined => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined

  const obj = parsed as Record<string, unknown>
  const isSubstantial = (v: unknown): v is string =>
    typeof v === 'string' && (v.length > 120 || v.includes('\n'))

  // An explicit markdown payload always wins.
  if (obj.format === 'markdown' && typeof obj.content === 'string' && obj.content.trim()) {
    return obj.content
  }

  for (const key of ['content', 'markdown', 'text', 'body']) {
    if (isSubstantial(obj[key])) return obj[key] as string
  }

  return undefined
}

/**
 * Detects whether the given text is a JSON object/array and, if so, returns it
 * pretty-printed inside a fenced ```json code block so markdown renderers show
 * it formatted. If the JSON wraps a human-readable markdown/text body, that body
 * is surfaced as markdown instead — far easier to read than an escaped JSON dump.
 * Text that is not valid JSON is returned unchanged, since tool call results are
 * not always JSON.
 */
export const formatJsonForMarkdown = (text: string): string => {
  if (typeof text !== 'string') return text

  const trimmed = text.trim()
  const firstChar = trimmed[0]
  if (firstChar !== '{' && firstChar !== '[') return text

  try {
    const parsed = JSON.parse(trimmed)
    if (parsed !== null && typeof parsed === 'object') {
      const body = extractReadableBody(parsed)
      if (body) return body
      return `\`\`\`json\n${JSON.stringify(parsed, null, 2)}\n\`\`\``
    }
  } catch {
    // Not valid JSON — leave the text unchanged.
  }

  return text
}

/**
 * Makes plain URLs (e.g. inside a JSON code block) clickable, since markdown
 * doesn't auto-link text inside code blocks. Only http/https links are matched
 * for safety, and existing <a> tags are skipped so they aren't wrapped twice.
 */
export const linkifyPlainUrls = (html: string): string => {
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g

  return html
    .split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi)
    .map((segment) => {
      if (/^<a\b/i.test(segment)) return segment

      return segment.replace(urlRegex, (url) => {
        // Strip common trailing punctuation (e.g. a period ending a sentence) so it
        // doesn't get swallowed into the link.
        const trailingMatch = url.match(/[.,;:)\]!?]+$/)
        const trailing = trailingMatch ? trailingMatch[0] : ''
        const cleanUrl = trailing ? url.slice(0, url.length - trailing.length) : url
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailing}`
      })
    })
    .join('')
}

export interface WebSearchResultItem {
  title: string
  link: string
  snippet?: string
}

/**
 * Detects the `{ Results: [{ Title, Link, Snippet }] }` shape returned by web search
 * tools and normalizes it into a compact source list, so results can be rendered as
 * linked source cards instead of a raw JSON dump. Returns null for any other shape.
 */
export const parseWebSearchResults = (text: string): WebSearchResultItem[] | null => {
  if (typeof text !== 'string') return null

  const trimmed = text.trim()
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return null

  try {
    const parsed = JSON.parse(trimmed)
    const list = parsed?.Results ?? parsed?.results
    if (!Array.isArray(list) || list.length === 0) return null

    const items: WebSearchResultItem[] = list
      .map((r: any) => ({
        title: r?.Title ?? r?.title,
        link: r?.Link ?? r?.link ?? r?.Url ?? r?.url,
        snippet: r?.Snippet ?? r?.snippet
      }))
      .filter((r: WebSearchResultItem) => Boolean(r.title) && Boolean(r.link))

    return items.length > 0 ? items : null
  } catch {
    return null
  }
}
